/**
 * 举报类型趋势图表组件 - 折线图/柱状图
 * 遵循 Opinion 图表开发规范
 */
import React, { useMemo, useState, useEffect } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import type { TRequestBackType } from '@op/utils/requestUtil';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { DBO_V, DBO_EC } from '@op/utils/format';
import useLanguage from '@op/hooks/useLanguage';
import { useChartFootBtn } from '@op/hooks/useChartFootBtn';
import { CATEGORY_CONFIG } from '../../../constants';
import type { ICategoryMentionsTrendResponse } from '@op/services/weeklyReport';
import EventWindows from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/Charts/BarAndLine/EventWindows';
import { ParamsTimeType } from '@op/types/common';
import { EventDataType } from '@op/hooks/useEventData';

// 分类列表项类型
type TCategoryListItem = ICategoryMentionsTrendResponse['list'][number];

// 默认颜色数组（用于其他类型）
const DEFAULT_COLORS = [
  '#5086F3',
  '#69D19C',
  '#6E7794',
  '#F5D242',
  '#7F6FF1',
  '#5EC8F1',
];

type TChartType = 'line' | 'bar';

interface IReportTypeTrendChartProps {
  data?: TRequestBackType;
  chartType: TChartType;
  selectedType: string | null;
  onChartClick?: (params: any) => void;
  filter?: any;
  isEn?: boolean;
  list?: TCategoryListItem[] | null;
  toFeeds?: () => void;
  eventData?: EventDataType;
}

const ReportTypeTrendChart: React.FC<IReportTypeTrendChartProps> = ({
  data,
  chartType,
  selectedType,
  onChartClick,
  filter = {},
  isEn = true,
  list = null,
  toFeeds,
  eventData = {
    eventList: {},
    chartRenderEventList: {},
    format: 'YYYY-MM-DD',
    isEmpty: true,
    bubbleEvent: {},
    getEventSeries: () => null,
    legendSelected: {},
    chartShowEventTopSpace: {
      daily: 60,
      weekly: 60,
      monthly: 130,
      hourly: 50,
    },
    hasEventBubbles: false,
  },
}) => {
  const { formatMessage } = useLanguage();

  // 图表实例和加载状态
  const [myChart, setMyChart] = useState<any>();
  const [loadingEvent, setLoadingEvent] = useState(true);

  // 使用 useChartFootBtn 生成底部按钮
  const { getBottomClickBtn, divElementRef } = useChartFootBtn({
    filter,
    buttons: [
      {
        key: 'feeds',
        labelId: 'Click to go to [Feeds]',
      },
    ],
    onClick: () => toFeeds?.(),
  });

  // 获取颜色 - 使用 CATEGORY_CONFIG
  const getColor = (name: string, index: number): string => {
    return (
      CATEGORY_CONFIG[name]?.color ||
      DEFAULT_COLORS[index % DEFAULT_COLORS.length]
    );
  };

  // 获取显示名称 - 根据 isEn 返回英文或中文名
  const getDisplayName = (nameEn: string): string => {
    if (isEn) return nameEn;
    const item = list?.find((i) => i.category_name_en === nameEn);
    return item?.category_name || nameEn;
  };

  const option = useMemo(() => {
    if (!data || data.isEmpty) return {};

    const legendData = data.legend || [];
    const xAxis = data.xAxis || [];

    // 根据图表类型生成 series 配置
    const series =
      data.series?.map((item, index) => {
        const isSelected = selectedType === item.name;
        const baseColor = getColor(item.name, index);

        if (chartType === 'bar') {
          // 柱状图配置 - 堆叠柱状图，选中态有深黄色 border
          const selectedBorderColor = '#D4A500';
          return {
            name: item.name,
            type: 'bar',
            stack: 'total',
            data: item.data,
            itemStyle: {
              color: selectedType && !isSelected ? `${baseColor}40` : baseColor,
              borderColor: isSelected ? selectedBorderColor : 'transparent',
              borderWidth: isSelected ? 2 : 0,
            },
            emphasis: {
              itemStyle: {
                color: baseColor,
                borderColor: selectedBorderColor,
                borderWidth: 2,
              },
            },
            barWidth: '60%',
          };
        }

        // 折线图配置 - 选中线高亮全部点展示，其他变成淡色
        const displayColor =
          selectedType && !isSelected ? `${baseColor}40` : baseColor;
        const lineWidth = isSelected ? 3 : 2;
        return {
          name: item.name,
          type: 'line',
          data: item.data,
          showSymbol: isSelected,
          lineStyle: {
            width: lineWidth,
            color: displayColor,
          },
          itemStyle: {
            color: displayColor,
          },
          z: isSelected ? 10 : 1,
        };
      }) || [];

    // 添加事件定位用的 dummy series
    const eventSeries = eventData.getEventSeries?.(chartType, xAxis.length);
    if (eventSeries) {
      series.push(eventSeries);
    }

    // 公共 tooltip 配置
    const tooltipConfig = {
      trigger: 'axis' as const,
      enterable: true,
      confine: false,
      position: (point: any, params: any, dom: any, rect: any, size: any) =>
        DBO_EC.enterablePosition(point, params, dom, rect, size, {
          verticalOffset: 30,
        }),
      // tooltip 显示在鼠标正上方，方便鼠标进入
      formatter: (params: any) =>
        formatChartParamsDate({
          params,
          title: DBO_EC.toolTime(
            params[0]?.axisValue,
            filter.date_type || 'daily',
          ),
          isSort: true,
          rightTitle: formatMessage({ id: 'Mentions' }),
          valueFormat: (value: number) => DBO_V.reviews(value),
          textShowFun: (_marker: string, seriesName: string) => {
            const color = getColor(seriesName, legendData.indexOf(seriesName));
            const displayName = getDisplayName(seriesName);
            return `<span style="display: flex; align-items: center; gap: 6px;">
                <span style="width: 8px; height: 8px; border-radius: 50%; background: ${color};"></span>
                <span style="color: #D8DAE4;">${displayName}</span>
              </span>`;
          },
          dealwithRecords: (records) => {
            return records.filter((item) => item.originText !== 'TopTipsEvent');
          },
          tablesConfigFunc: (records: any) => [
            {
              showHeader: false,
              records,
              columns: [
                { key: 'text' },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                },
              ],
            },
          ],
          footerText: getBottomClickBtn({
            categories: params[0]?.axisValue || '',
          }),
          className: 'chart-formatter-custom-footer',
        }),
    };

    // 公共 legend 配置
    const legendConfig = {
      data: legendData.map((name, index) => ({
        name,
        itemStyle: { color: getColor(name, index) },
      })),
      bottom: 0,
      type: 'scroll' as const,
      formatter: (name: string) => getDisplayName(name),
      selected: eventData.legendSelected,
    };

    // 公共 xAxis 配置
    const xAxisConfig = {
      type: 'category' as const,
      data: xAxis,
      axisLabel: {
        formatter: (v: string) => DBO_EC.timeX(v, filter.date_type),
      },
      axisLine: { lineStyle: { color: '#E5E5E5' } },
      axisTick: { show: false },
    };

    // 公共 yAxis 配置
    const yAxisConfig = {
      type: 'value' as const,
      axisLabel: { formatter: DBO_EC.reviews },
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#F0F0F0' } },
    };

    // 有事件气泡时增加顶部空间
    const dateType: ParamsTimeType = filter?.date_type || 'daily';

    return {
      tooltip: tooltipConfig,
      legend: legendConfig,
      grid: {
        top: eventData.hasEventBubbles
          ? eventData.chartShowEventTopSpace[dateType]
          : 20,
      },
      xAxis: xAxisConfig,
      yAxis: yAxisConfig,
      series,
    };
  }, [
    data,
    chartType,
    selectedType,
    formatMessage,
    isEn,
    list,
    eventData.chartRenderEventList,
    filter,
  ]);

  const eventsDict = useMemo(() => {
    const events: Record<string, Function> = {};
    if (onChartClick) {
      events.click = onChartClick;
    }
    return events;
  }, [onChartClick]);

  // 处理图表加载完成事件
  const handleChartReady = (chart: any) => {
    if (chart) {
      const echartsInstance = chart.getEchartsInstance?.() || chart;
      setMyChart(echartsInstance);

      // 监听 finished 事件来显示事件气泡
      echartsInstance.on('finished', () => {
        setTimeout(() => {
          setLoadingEvent(false);
        }, 200);
      });
    }
  };

  // 重置加载状态
  useEffect(() => {
    setLoadingEvent(true);
  }, [chartType, eventData.chartRenderEventList]);

  // 从 hook 中获取的 bubbleEvent 数据
  const bubbleEvent = eventData.bubbleEvent || {};

  const xAxis = data?.xAxis as string[] | undefined;
  const maxTimeValue = xAxis && xAxis.length > 0 ? xAxis[xAxis.length - 1] : '';

  // 判断是否显示事件气泡
  const shouldShowEventBubbles =
    !loadingEvent && myChart && eventData.hasEventBubbles;

  return (
    <div ref={divElementRef} style={{ position: 'relative' }}>
      <EChartCommon
        options={option}
        height={350}
        optionsProps={{ type: chartType }}
        eventsDict={eventsDict}
        onChartReady={handleChartReady}
      />
      {/* 事件气泡 */}
      {shouldShowEventBubbles && (
        <EventWindows
          myChart={myChart}
          eventList={bubbleEvent}
          format={eventData.format}
          maxTimeValue={maxTimeValue}
          showEventAxis={false}
          dateType={filter?.date_type || 'daily'}
          xAxisData={xAxis || []}
        />
      )}
    </div>
  );
};

export default ReportTypeTrendChart;
